import { useRef, useEffect, useCallback } from 'react';
import { analyticsAllowed } from '../../utils/gdprConsent';

/**
 * Client-side analytics batching hook
 * Collects page views in memory and sends them in batches.
 * Only tracks when GDPR consent includes analytics.
 */
const useAnalytics = () => {
  const eventsRef = useRef([]);
  const lastFlushRef = useRef(Date.now());
  
  // Flush events to the server
  const flush = useCallback(() => {
    if (eventsRef.current.length === 0) return;
    
    const events = [...eventsRef.current];
    eventsRef.current = [];
    lastFlushRef.current = Date.now();
    
    const API_BASE = process.env.REACT_APP_API_URL || 'https://api.vltryon.com/api';
    const url = `${API_BASE}/analytics/batch`;
    const body = JSON.stringify({ events });
    
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  }, []);
  
  // Track a page view (batched; only if analytics consent given)
  const trackPageView = useCallback((page) => {
    if (!analyticsAllowed()) return;
    eventsRef.current.push({
      page,
      timestamp: Date.now(),
    });
  }, []);
  
  // Set up flushing: unload/visibility/unmount + once per 24h
  useEffect(() => {
    const handleUnload = () => flush();
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const interval = setInterval(() => {
      if (eventsRef.current.length > 0) flush();
    }, MS_PER_DAY);
    
    const onConsentUpdated = () => {
      if (!analyticsAllowed()) eventsRef.current = [];
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('gdpr-consent-updated', onConsentUpdated);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('gdpr-consent-updated', onConsentUpdated);
      clearInterval(interval);
      if (eventsRef.current.length > 0) flush();
    };
  }, [flush]);
  
  return { trackPageView, flush };
};

export default useAnalytics;
